import React from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle, Clock, Search, Landmark, X, ArrowRight } from 'lucide-react'
import { Button } from '../ui/Button'
import { cn } from '@/lib/utils'
import { urgencyTier } from '../../lib/identification/countdown'

interface SalesCompletionNextStepProps {
  open: boolean
  idDaysRemaining: number
  onGoToIdHub: () => void
  onGoToEscrowHub: () => void
  onClose: () => void
}

const URGENCY_COLORS: Record<'critical' | 'warning' | 'safe', string> = {
  critical: 'text-red-600 dark:text-red-400',
  warning: 'text-amber-500 dark:text-amber-400',
  safe: 'text-green-600 dark:text-green-400',
}

const URGENCY_BG: Record<'critical' | 'warning' | 'safe', string> = {
  critical: 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700',
  warning: 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700',
  safe: 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-700',
}

export const SalesCompletionNextStep: React.FC<SalesCompletionNextStepProps> = ({
  open,
  idDaysRemaining,
  onGoToIdHub,
  onGoToEscrowHub,
  onClose,
}) => {
  if (!open) return null

  const tier = urgencyTier(idDaysRemaining)
  const urgencyColor = URGENCY_COLORS[tier]
  const urgencyBg = URGENCY_BG[tier]

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Sales Hub complete — next steps"
    >
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-2xl w-full max-w-lg flex flex-col overflow-hidden">
        <div className="bg-gradient-to-br from-green-600 to-emerald-500 px-6 py-5 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/20 rounded-xl">
              <CheckCircle className="h-7 w-7 text-white" />
            </div>
            <div>
              <p className="text-green-100 text-xs font-medium uppercase tracking-wider">Sales Hub Complete</p>
              <h2 className="text-white text-xl font-bold leading-tight mt-0.5">
                Your exchange is live.
              </h2>
            </div>
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="p-1.5 rounded-lg hover:bg-white/20 transition-colors text-white/80 hover:text-white flex-shrink-0"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-4">
          <div className={cn('flex items-center gap-4 rounded-xl border-2 px-4 py-4', urgencyBg)}>
            <Clock className={cn('h-10 w-10 flex-shrink-0', urgencyColor)} aria-hidden="true" />
            <div className="min-w-0">
              <p className="text-slate-500 dark:text-slate-400 text-xs font-medium uppercase tracking-wide">
                45-Day Identification Window
              </p>
              <p className={cn('text-4xl font-extrabold tabular-nums leading-none mt-0.5', urgencyColor)}>
                {idDaysRemaining}
                <span className="text-base font-semibold ml-1">days left</span>
              </p>
              <p className="text-slate-600 dark:text-slate-300 text-sm mt-1 leading-snug">
                {tier === 'critical'
                  ? 'Deadline is very close — identify your replacement property now.'
                  : tier === 'warning'
                  ? 'Less than three weeks remain. Start identifying replacement properties.'
                  : 'Your 45-day identification window is running. IRS requires you to identify replacement properties within this window.'}
              </p>
            </div>
          </div>

          <div className="space-y-3">
            <p className="text-slate-700 dark:text-slate-200 text-sm font-semibold">What would you like to do next?</p>

            <button
              onClick={onGoToIdHub}
              aria-label="Go to Identification Hub — recommended next step"
              className={cn(
                'w-full text-left rounded-xl border-2 border-blue-500 bg-blue-50 dark:bg-blue-900/20 px-4 py-4',
                'hover:bg-blue-100 dark:hover:bg-blue-900/40 transition-colors group focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2'
              )}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-600 rounded-lg flex-shrink-0">
                  <Search className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-blue-700 dark:text-blue-300 font-semibold text-sm">
                      Identify Replacement Properties
                    </span>
                    <span className="text-xs bg-blue-600 text-white font-medium px-2 py-0.5 rounded-full">
                      Recommended
                    </span>
                  </div>
                  <p className="text-blue-600 dark:text-blue-400 text-xs mt-0.5 leading-snug">
                    The 45-day clock is ticking — identifying properties is the hard deadline and must be done first.
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-blue-500 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>

            <button
              onClick={onGoToEscrowHub}
              aria-label="Go to Escrow Hub"
              className={cn(
                'w-full text-left rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50 dark:bg-slate-700/30 px-4 py-4',
                'hover:bg-slate-100 dark:hover:bg-slate-700/60 transition-colors group focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2'
              )}
            >
              <div className="flex items-center gap-3">
                <div className="p-2 bg-slate-600 rounded-lg flex-shrink-0">
                  <Landmark className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-slate-700 dark:text-slate-200 font-semibold text-sm">
                    Set Up Qualified Escrow
                  </span>
                  <p className="text-slate-500 dark:text-slate-400 text-xs mt-0.5 leading-snug">
                    Confirm QI custody and wire funds — can run in parallel with identification.
                  </p>
                </div>
                <ArrowRight className="h-4 w-4 text-slate-400 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
          </div>
        </div>

        <div className="border-t border-slate-100 dark:border-slate-700 px-6 py-3 flex items-center justify-between gap-4">
          <p className="text-xs text-slate-400 dark:text-slate-500 leading-snug">
            This is general information about 1031 timing, not tax advice. Consult your tax advisor.
          </p>
          <Button
            variant="ghost"
            onClick={onClose}
            className="text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 text-sm flex-shrink-0"
          >
            I'll decide later
          </Button>
        </div>
      </div>
    </div>,
    document.body
  )
}
